import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Sparkles, Loader2, Brain, Activity, Eye, Ear, Target,
  Wand2, CheckCircle2, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const DISABILITIES = [
  { key: 'autism', ar: 'اضطراب طيف التوحد', en: 'Autism' },
  { key: 'intellectual', ar: 'إعاقة ذهنية', en: 'Intellectual' },
  { key: 'physical', ar: 'إعاقة حركية', en: 'Physical' },
  { key: 'visual', ar: 'إعاقة بصرية', en: 'Visual' },
  { key: 'hearing', ar: 'إعاقة سمعية', en: 'Hearing' },
];

export default function ExerciseGeneratorPage() {
  const { lang } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ age: 10, disability: 'autism', motor_level: 'intermediate', objective: '', available_tools: '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.objective.trim()) { toast.error(lang === 'ar' ? 'أدخل الهدف التعليمي' : 'Enter the learning objective'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post('/exercises/generate', { ...form, language: lang });
      setResult(r.data);
      toast.success(lang === 'ar' ? 'تم توليد التمرين بنجاح' : 'Exercise generated');
    } catch (e) { toast.error(e.response?.data?.detail || 'AI error'); }
    setBusy(false);
  };

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="exo-gen-page">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/exercises"><ArrowLeft className={`w-4 h-4 me-1 ${lang === 'ar' ? 'rotate-180' : ''}`} />{lang === 'ar' ? 'المكتبة' : 'Library'}</Link>
      </Button>

      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 grid place-items-center text-white"><Wand2 className="w-6 h-6" /></div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-black">{lang === 'ar' ? 'مولّد التمارين الذكي' : 'Smart Exercise Generator'}</h1>
          <p className="text-xs text-muted-foreground">{lang === 'ar' ? 'أدخل ملف الطالب وسيقوم الذكاء الاصطناعي بتصميم تمرين كامل وخطة درس ونشاط منزلي.' : 'Provide the student profile and AI will design a full exercise, lesson plan and home activity.'}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Brain className="w-5 h-5 text-indigo-600" />{lang === 'ar' ? 'ملف الطالب' : 'Student profile'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-3" onSubmit={submit}>
              <div>
                <Label className="text-xs">{lang === 'ar' ? 'العمر' : 'Age'}</Label>
                <Input type="number" min={3} max={22} value={form.age} onChange={e => setForm({ ...form, age: Number(e.target.value) })} data-testid="gen-age" />
              </div>

              <div>
                <Label className="text-xs">{lang === 'ar' ? 'نوع الإعاقة' : 'Disability'}</Label>
                <Select value={form.disability} onValueChange={(v) => setForm({ ...form, disability: v })}>
                  <SelectTrigger data-testid="gen-disability"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISABILITIES.map(d => <SelectItem key={d.key} value={d.key}>{lang === 'ar' ? d.ar : d.en}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">{lang === 'ar' ? 'المستوى الحركي' : 'Motor level'}</Label>
                <Select value={form.motor_level} onValueChange={(v) => setForm({ ...form, motor_level: v })}>
                  <SelectTrigger data-testid="gen-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">{lang === 'ar' ? 'مبتدئ' : 'Beginner'}</SelectItem>
                    <SelectItem value="intermediate">{lang === 'ar' ? 'متوسط' : 'Intermediate'}</SelectItem>
                    <SelectItem value="advanced">{lang === 'ar' ? 'متقدم' : 'Advanced'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">{lang === 'ar' ? 'الهدف التعليمي' : 'Learning objective'}</Label>
                <Textarea value={form.objective} onChange={e => setForm({ ...form, objective: e.target.value })} placeholder={lang === 'ar' ? 'مثال: تحسين التوازن على قدم واحدة 10 ثوانٍ' : 'e.g. improve single-leg balance for 10s'} rows={2} data-testid="gen-objective" />
              </div>

              <div>
                <Label className="text-xs">{lang === 'ar' ? 'الأدوات المتاحة' : 'Available tools'}</Label>
                <Input value={form.available_tools} onChange={e => setForm({ ...form, available_tools: e.target.value })} placeholder={lang === 'ar' ? 'كرة، سجادة، شريط...' : 'ball, mat, tape...'} data-testid="gen-tools" />
              </div>

              <Button type="submit" disabled={busy} className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:opacity-90" data-testid="gen-submit">
                {busy ? <><Loader2 className="w-4 h-4 me-2 animate-spin" />{lang === 'ar' ? 'يولّد...' : 'Generating...'}</> : <><Sparkles className="w-4 h-4 me-2" />{lang === 'ar' ? 'ولّد التمرين' : 'Generate'}</>}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Result */}
        <div className="lg:col-span-2">
          {!result && !busy && (
            <Card className="border-dashed border-2"><CardContent className="p-12 text-center">
              <Sparkles className="w-12 h-12 text-indigo-400/40 mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">{lang === 'ar' ? 'ستظهر النتيجة هنا بعد التوليد.' : 'Generated result will appear here.'}</p>
            </CardContent></Card>
          )}
          {busy && (
            <Card><CardContent className="p-12 text-center" data-testid="gen-busy">
              <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{lang === 'ar' ? 'يقوم الذكاء الاصطناعي بتصميم تمرين مخصص... قد يستغرق 10-20 ثانية.' : 'AI is designing a personalized exercise... 10-20s.'}</p>
            </CardContent></Card>
          )}
          {result && (
            <Card data-testid="gen-result">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-violet-50 border-b border-border">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge className="bg-indigo-600 text-white text-[11px]">{lang === 'ar' ? result.skill_type_ar : (result.skill_type_en || result.skill_type_ar)}</Badge>
                      <Badge variant="outline" className="text-[11px]">{result.level}</Badge>
                      <Badge className="bg-amber-500 text-white text-[11px]"><Sparkles className="w-3 h-3 me-0.5" />AI</Badge>
                    </div>
                    <CardTitle className="text-xl">{lang === 'ar' ? result.name_ar : (result.name_en || result.name_ar)}</CardTitle>
                  </div>
                  <Button asChild size="sm" className="bg-indigo-600"><Link to={`/exercises/${result.id}`} data-testid="open-generated">{lang === 'ar' ? 'فتح البطاقة' : 'Open card'}<ChevronRight className="w-4 h-4 ms-1 rtl:rotate-180" /></Link></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <Section title={lang === 'ar' ? '🎯 الهدف' : '🎯 Objective'} text={lang === 'ar' ? result.objective_ar : (result.objective_en || result.objective_ar)} />
                <Section title={lang === 'ar' ? '🛠 الأدوات' : '🛠 Tools'} text={lang === 'ar' ? result.tools_ar : (result.tools_en || result.tools_ar)} />
                <div>
                  <SectionTitle>{lang === 'ar' ? '👣 الخطوات' : '👣 Steps'}</SectionTitle>
                  <ol className="space-y-1.5 text-sm" data-testid="gen-steps">
                    {((lang === 'ar' ? result.steps_ar : (result.steps_en?.length ? result.steps_en : result.steps_ar)) || []).map((s, i) => (
                      <li key={i} className="flex items-start gap-2"><span className="w-5 h-5 rounded-full bg-indigo-600 text-white text-[10px] font-black grid place-items-center shrink-0 mt-0.5">{i + 1}</span>{s}</li>
                    ))}
                  </ol>
                </div>
                <Section title={lang === 'ar' ? '🛡 السلامة' : '🛡 Safety'} text={lang === 'ar' ? result.safety_ar : (result.safety_en || result.safety_ar)} />
                <Section title={lang === 'ar' ? '✅ معايير النجاح' : '✅ Success criteria'} text={lang === 'ar' ? result.success_criteria_ar : (result.success_criteria_en || result.success_criteria_ar)} />
                {Object.keys(result.modifications || {}).length > 0 && (
                  <div>
                    <SectionTitle>{lang === 'ar' ? '🔧 تعديلات حسب الإعاقة' : '🔧 Modifications'}</SectionTitle>
                    <ul className="space-y-1 text-sm">
                      {Object.entries(result.modifications).map(([k, v]) => <li key={k}><b className="text-indigo-700">{k}:</b> {v}</li>)}
                    </ul>
                  </div>
                )}
                {(result.lesson_plan_ar || result.lesson_plan_en) && (
                  <div className="bg-indigo-50/50 border border-indigo-200 rounded-lg p-3">
                    <SectionTitle>{lang === 'ar' ? '📚 خطة درس قصيرة' : '📚 Mini lesson plan'}</SectionTitle>
                    <div className="text-sm whitespace-pre-wrap">{lang === 'ar' ? result.lesson_plan_ar : result.lesson_plan_en}</div>
                  </div>
                )}
                {(result.home_activity_ar || result.home_activity_en) && (
                  <div className="bg-emerald-50/50 border border-emerald-200 rounded-lg p-3">
                    <SectionTitle>{lang === 'ar' ? '🏠 نشاط منزلي للأسرة' : '🏠 Home activity'}</SectionTitle>
                    <div className="text-sm">{lang === 'ar' ? result.home_activity_ar : result.home_activity_en}</div>
                  </div>
                )}
                <div className="border-t border-border pt-3 flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  {lang === 'ar' ? 'تم حفظ التمرين في المكتبة.' : 'Exercise saved to the library.'}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </main>
  );
}

function SectionTitle({ children }) {
  return <div className="font-bold text-xs uppercase tracking-wider text-indigo-700 mb-1.5">{children}</div>;
}
function Section({ title, text }) {
  return text ? <div><SectionTitle>{title}</SectionTitle><div className="text-sm leading-relaxed">{text}</div></div> : null;
}
